import React from 'react';

const section = (bg) => ({
  padding: '100px 0',
  background: bg || 'transparent',
});

const wrap = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 32px',
};

const eyebrow = {
  display: 'inline-block',
  background: 'rgba(45,106,79,0.12)',
  border: '1px solid rgba(82,183,136,0.2)',
  borderRadius: '100px',
  padding: '5px 16px',
  fontSize: '12px',
  color: '#52b788',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  marginBottom: '20px',
};

const h2 = {
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: 'clamp(32px,4vw,52px)',
  fontWeight: 800,
  letterSpacing: '-1.5px',
  marginBottom: '16px',
};

const lead = {
  fontSize: '17px',
  color: '#a8b4cc',
  maxWidth: '620px',
  lineHeight: 1.7,
};

const card = {
  background: '#141c35',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '16px',
  padding: '28px 24px',
};

const small = {
  color: '#a8b4cc',
  fontSize: '14px',
  lineHeight: 1.65,
};

const title = {
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '18px',
  fontWeight: 700,
  color: '#fff',
  marginBottom: '10px',
};

const button = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #2d6a4f, #40916c)',
  color: '#fff',
  padding: '14px 24px',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 700,
  border: 'none',
  boxShadow: '0 8px 32px rgba(45,106,79,0.35)',
};

const chips = ['Modules overview', 'Technical snapshot', 'Implementation path', 'Pricing approach'];

const specGroups = [
  {
    name: 'Platform',
    items: ['Web-based ITSM workspace', 'Self-service portal for end users', 'Agent console with queues and SLA context', 'Manager views for approvals and reporting'],
  },
  {
    name: 'Security',
    items: ['Role-based access control', 'Module-level permissions', 'Audit trail for operational changes', 'SSO-ready with directory integration'],
  },
  {
    name: 'Workflow',
    items: ['Configurable forms and categories', 'SLA policies by priority and ticket type', 'Auto-routing and escalation rules', 'CAB-ready change approvals'],
  },
  {
    name: 'Integration',
    items: ['Email notification workflows', 'Active Directory or LDAP alignment', 'Webhook-ready event delivery', 'Exportable reports and audit data'],
  },
];

const implementation = [
  ['01', 'Discover', 'Map service catalog, roles, teams, ticket categories, SLAs, approval paths, and reporting needs.'],
  ['02', 'Configure', 'Set up modules, forms, queues, RBAC, notification rules, dashboards, and service-level policies.'],
  ['03', 'Integrate', 'Connect identity, email, data exports, webhooks, and any essential migration from current tools.'],
  ['04', 'Launch', 'Pilot with one IT team, tune workflows, train users, and move into production with go-live support.'],
];

const useCases = [
  ['Enterprise IT Helpdesk', 'Centralize service requests and incidents across departments with SLA visibility for every ticket.'],
  ['Change Governance', 'Run normal, standard, and emergency changes with approvals, risk context, rollback plans, and audit trails.'],
  ['Problem Reduction', 'Link recurring incidents to root-cause investigations and known-error knowledge articles.'],
  ['Internal Product Support', 'Track defects and enhancements beside incidents so business and engineering teams share context.'],
  ['Asset and CMDB Control', 'Connect configuration items to incidents, changes, services, and dependent systems.'],
  ['Compliance-Ready Operations', 'Keep role changes, ticket updates, and approvals searchable for audit and management review.'],
];

const pricingTable = {
  company: 'ATLAS',
  rows: [
    { product: 'ITSM', desc: 'ITSM', priceINR: '₹950.00', priceUSD: '$10.0', users: '200' },
    { product: 'Asset Management', desc: 'Asset Management, Discovery', priceINR: '₹950.00', priceUSD: '$10.0', users: '200' },
  ],
};

const priceCols = ['Company', 'Product', 'Description', 'Price (INR)', 'Price (USD)', 'Users', ''];

const Line = ({ children }) => (
  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', color: '#c8d4e8', fontSize: '14px', lineHeight: 1.6 }}>
    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#52b788', marginTop: '8px', flexShrink: 0 }} />
    <span>{children}</span>
  </li>
);

function scrollToDemo() {
  const el = document.getElementById('book-a-demo');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

export default function ProductDetails() {
  const datasheetHref = `${process.env.PUBLIC_URL}/atlas-itsm-datasheet.pdf`;

  return (
    <>
      <section id="data-sheet" style={section('rgba(255,255,255,0.01)')}>
        <div style={wrap}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '48px', alignItems: 'center' }} className="atlas-two-col">
            <div>
              <div style={eyebrow}>Downloadable Data Sheet</div>
              <h2 style={h2}>A buyer-ready ATLAS ITSM summary</h2>
              <p style={lead}>
                The downloadable PDF captures the product modules, workflow capabilities, technical snapshot, implementation path, and pricing approach in one concise page for internal review.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', margin: '28px 0 34px' }}>
                {chips.map(c => (
                  <span key={c} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '100px', padding: '7px 13px', color: '#a8b4cc', fontSize: '12px', fontWeight: 600 }}>{c}</span>
                ))}
              </div>
              <a href={datasheetHref} download="atlas-itsm-datasheet.pdf" style={button}>Download Data Sheet PDF</a>
            </div>
            <div style={{ ...card, padding: '0', overflow: 'hidden' }}>
              <div style={{ background: '#0f1628', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '16px 20px', color: '#52b788', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Data Sheet Preview</div>
              <div style={{ padding: '26px 24px' }}>
                <h3 style={{ ...title, fontSize: '24px' }}>ATLAS ITSM</h3>
                <p style={{ ...small, marginBottom: '24px' }}>Enterprise IT Service Management by Aiprus Software.</p>
                {['8 ITSM modules', 'SLA, RBAC, audit, notifications', 'Private or managed deployment', 'Quote-based rollout options'].map(item => (
                  <div key={item} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '14px 0' }}>
                    <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{item}</span>
                    <span style={{ color: '#52b788', fontSize: '13px', fontWeight: 700 }}>Included</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="technical-specifications" style={section()}>
        <div style={wrap}>
          <div style={{ textAlign: 'center', marginBottom: '58px' }}>
            <div style={eyebrow}>Technical Specifications</div>
            <h2 style={h2}>Built for controlled IT operations</h2>
            <p style={{ ...lead, margin: '0 auto' }}>
              ATLAS ITSM combines workflow governance, user access control, automation, and integration readiness so the platform can fit into existing enterprise IT environments.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '18px' }} className="atlas-four-grid">
            {specGroups.map(group => (
              <div key={group.name} style={card}>
                <h3 style={title}>{group.name}</h3>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {group.items.map(item => <Line key={item}>{item}</Line>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="implementation" style={section('rgba(255,255,255,0.01)')}>
        <div style={wrap}>
          <div style={{ marginBottom: '58px' }}>
            <div style={eyebrow}>Implementation</div>
            <h2 style={h2}>A practical path from current process to live service desk</h2>
            <p style={lead}>
              ATLAS can start with a focused service-desk launch, then expand into change, problem, knowledge, defect, enhancement, and CMDB workflows as teams mature.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '18px' }} className="atlas-four-grid">
            {implementation.map(([num, name, desc]) => (
              <div key={num} style={card}>
                <div style={{ color: '#52b788', fontFamily: "'Space Grotesk', sans-serif", fontSize: '28px', fontWeight: 800, marginBottom: '12px' }}>{num}</div>
                <h3 style={title}>{name}</h3>
                <p style={small}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="use-cases" style={section()}>
        <div style={wrap}>
          <div style={{ textAlign: 'center', marginBottom: '58px' }}>
            <div style={eyebrow}>Use Cases</div>
            <h2 style={h2}>Where ATLAS ITSM fits best</h2>
            <p style={{ ...lead, margin: '0 auto' }}>
              The platform is relevant wherever IT teams need consistent intake, ownership, approvals, root-cause tracking, and service transparency.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }} className="atlas-three-grid">
            {useCases.map(([name, desc]) => (
              <div key={name} style={card}>
                <h3 style={title}>{name}</h3>
                <p style={small}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="architecture" style={section('rgba(255,255,255,0.01)')}>
        <div style={wrap}>
          <div style={{ textAlign: 'center', marginBottom: '58px' }}>
            <div style={eyebrow}>Architecture Diagram</div>
            <h2 style={h2}>How ATLAS connects users, workflows, and governance</h2>
            <p style={{ ...lead, margin: '0 auto' }}>
              The architecture centers on the ITSM workflow engine, with controlled access from users and agents, integrations at the edge, and audit-ready data services underneath.
            </p>
          </div>
          <div style={{ ...card, padding: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: '18px', alignItems: 'stretch' }} className="architecture-grid">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {['End User Portal', 'Agent Workspace', 'Admin Console'].map(item => (
                  <div key={item} style={{ background: '#0f1628', border: '1px solid rgba(82,183,136,0.22)', borderRadius: '12px', padding: '16px', color: '#fff', fontWeight: 700, textAlign: 'center' }}>{item}</div>
                ))}
              </div>
              <div style={{ background: 'linear-gradient(135deg, rgba(45,106,79,0.25), rgba(15,22,40,0.9))', border: '1px solid rgba(82,183,136,0.35)', borderRadius: '14px', padding: '20px' }}>
                <div style={{ color: '#52b788', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px', textAlign: 'center' }}>ATLAS ITSM Application Layer</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="architecture-inner-grid">
                  {['Ticket Workflow Engine', 'SLA and Escalation', 'Approval and CAB Flow', 'Knowledge and CMDB', 'Notification Service', 'Reporting Dashboards'].map(item => (
                    <div key={item} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '13px', color: '#c8d4e8', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>{item}</div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {['Directory and SSO', 'Email and Webhooks', 'Exports and BI', 'ITSM Database', 'Audit Log'].map(item => (
                  <div key={item} style={{ background: '#0f1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px 16px', color: '#a8b4cc', fontWeight: 700, textAlign: 'center' }}>{item}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" style={section()}>
        <div style={wrap}>
          <div style={{ textAlign: 'center', marginBottom: '58px' }}>
            <div style={eyebrow}>Pricing</div>
            <h2 style={h2}>Pricing aligned to your rollout</h2>
            <p style={{ ...lead, margin: '0 auto' }}>
              ATLAS is priced per module, so teams can start with what they need and add capabilities as they scale.
            </p>
          </div>
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div className="pricing-table-scroll" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                <thead>
                  <tr>
                    {priceCols.map(h => (
                      <th
                        key={h || 'action'}
                        style={{
                          textAlign: 'left',
                          padding: '16px 20px',
                          fontSize: '12px',
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: '#52b788',
                          background: '#0f1628',
                          borderBottom: '1px solid rgba(255,255,255,0.08)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pricingTable.rows.map((row, i) => (
                    <tr
                      key={row.product}
                      style={{ borderBottom: i === pricingTable.rows.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)' }}
                    >
                      {i === 0 && (
                        <td
                          rowSpan={pricingTable.rows.length}
                          style={{
                            padding: '20px',
                            verticalAlign: 'middle',
                            color: '#fff',
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontWeight: 800,
                            fontSize: '16px',
                            borderRight: '1px solid rgba(255,255,255,0.06)',
                            background: 'rgba(82,183,136,0.05)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {pricingTable.company}
                        </td>
                      )}
                      <td style={{ padding: '20px', color: '#fff', fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>{row.product}</td>
                      <td style={{ padding: '20px', color: '#a8b4cc', fontSize: '14px', lineHeight: 1.6, minWidth: '220px' }}>{row.desc}</td>
                      <td style={{ padding: '20px', color: '#fff', fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>{row.priceINR}</td>
                      <td style={{ padding: '20px', color: '#fff', fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>{row.priceUSD}</td>
                      <td style={{ padding: '20px', color: '#c8d4e8', fontSize: '14px', whiteSpace: 'nowrap' }}>{row.users}</td>
                      <td style={{ padding: '20px' }}>
                        <button
                          type="button"
                          onClick={scrollToDemo}
                          style={{ ...button, padding: '9px 18px', fontSize: '13px', boxShadow: 'none', whiteSpace: 'nowrap' }}
                        >
                          Contact Us
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p style={{ ...small, textAlign: 'center', marginTop: '20px' }}>
            Pricing shown per module. Contact us for volume discounts, private deployment, and enterprise rollout quotes.
          </p>
        </div>
      </section>

      <style>{`
        @media(max-width:1000px){
          .atlas-four-grid{ grid-template-columns:repeat(2,1fr) !important; }
          .atlas-two-col{ grid-template-columns:1fr !important; }
          .architecture-grid{ grid-template-columns:1fr !important; }
        }
        @media(max-width:760px){
          .atlas-three-grid{ grid-template-columns:1fr !important; }
          .atlas-four-grid{ grid-template-columns:1fr !important; }
          .architecture-inner-grid{ grid-template-columns:1fr !important; }
        }
        .pricing-table-scroll{ -webkit-overflow-scrolling:touch; }
        .pricing-table-scroll table tbody tr:hover{ background:rgba(255,255,255,0.02); }
      `}</style>
    </>
  );
}
